"use client";

import { CircularProgress } from "@mui/material";
import { Formik } from "formik";
import React from "react";
import { useParams } from "react-router-dom";

import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import RenewForm from "@/components/templates/RenewForm";
import { determineInitialFilterValues2 } from "@/components/templates/RenewForm/components/ProductInfoCard/constants";
import {
  buildFormInitialData,
  buildFormSchema,
  normalizeData,
} from "@/components/templates/RenewForm/form-data";
import { SubscribeFormData } from "@/components/templates/RenewForm/types";
import {
  defaultPageNumber as pageNumber,
  defaultPageSize as pageSize,
} from "@/constants/pagination";
import useGetCustomerById from "@/hooks/requests/customer/use-get-customer-by-id";
import useGetCustomerSalesProductPayments from "@/hooks/requests/customer-sales-product-payments/use-get-customer-sales-product-payments";
import useCreateRenew from "@/hooks/requests/renew/use-create-renew";
import useGetRenewTotalById from "@/hooks/requests/renew/use-get-renew-total-by-id";
import useGetSalesProductById from "@/hooks/requests/sales-product/use-get-sales-product-by-id";
import useGetSalesProductPriceById from "@/hooks/requests/sales-product-price/use-get-sales-product-price-by-id";
import useFilters from "@/hooks/use-filters";
import useGetCustomerId from "@/hooks/use-get-customer-id";
import { ICustomerSalesProductPayment } from "@/interfaces/customer-sales-product-payment.interfaces";
import { RenewMessages } from "@/messages/renew/renew.messages";

type RenewPageProps = {
  messages: RenewMessages;
};

export default function RenewPage({ messages }: RenewPageProps) {
  const { salesproductpriceId } = useParams();
  const productpriceId = Number(salesproductpriceId) || 0;
  const customerId = useGetCustomerId();
  const _id = Number(customerId) || 0;
  const { data: customer } = useGetCustomerById({ _id });
  const { create, isLoading: isLoadingCreateRenew } = useCreateRenew();

  const {
    data: salesProductPrice,
    isLoading: isLoadingSalesProductPrice,
    error: errorSalesProductPrice,
  } = useGetSalesProductPriceById({ _id: productpriceId });

  const {
    data: salesProduct,
    isLoading: isLoadingSalesProduct,
    error: errorSalesProduct,
  } = useGetSalesProductById({
    _id: salesProductPrice ? salesProductPrice.salesProductId : 0,
  });

  const initialFilters = salesProductPrice
    ? determineInitialFilterValues2(salesProductPrice.id)
    : [];

  const filter = useFilters<ICustomerSalesProductPayment>({ initialValue: initialFilters });
  const filters = filter.filters;

  const { data: customerSalesPayment } = useGetCustomerSalesProductPayments({
    filters,
    pageNumber,
    pageSize,
  });

  const customerSalesProductPayment = customerSalesPayment?.items[0] || null;

  const { data: totalPayment } = useGetRenewTotalById({
    _id: Number(customerSalesProductPayment?.customerSalesProductId) || 0,
  });

  const isLoading = isLoadingSalesProductPrice || isLoadingSalesProduct || isLoadingCreateRenew;
  const error = errorSalesProduct || errorSalesProductPrice;
  const shouldLoadCards = !!salesProduct && !!salesProductPrice;
  const shouldLoadError = !isLoading && !!error;

  const handleSubmit = async (_formData: SubscribeFormData) => {
    if (!customer || !salesProduct || !salesProductPrice) return;

    const successMessage = messages.planRenewSuccess;
    const errorMessage = messages.planRenewError;

    const formData = normalizeData(_formData, customer, salesProduct, salesProductPrice);

    const response = await create({ formData, successMessage, errorMessage });

    if (response && response.invoiceUrl) window.location.href = response.invoiceUrl;
  };

  return (
    <PageLayout messages={messages} title={messages.title}>
      <PageContainer>
        <Suspense fallback={<CircularProgress />} isSuspended={isLoading}>
          {customer && customerSalesProductPayment && totalPayment ? (
            <Formik
              initialValues={buildFormInitialData(
                customer,
                customerSalesProductPayment,
                totalPayment
              )}
              validationSchema={buildFormSchema(messages)}
              validateOnChange={true}
              onSubmit={handleSubmit}
            >
              {shouldLoadCards && (
                <RenewForm
                  messages={messages}
                  salesProduct={salesProduct}
                  salesProductPrice={salesProductPrice}
                  totalPayment={totalPayment}
                  isLoading={isLoading}
                />
              )}
            </Formik>
          ) : (
            <CircularProgress />
          )}

          {!!shouldLoadError && (
            <AlertBlock
              title={messages.salesProductErrorToLoad}
              description={error.message}
              alt={messages.salesProductNotFoundAlt}
            />
          )}
        </Suspense>
      </PageContainer>
    </PageLayout>
  );
}
